import { useAppSelector } from '@hooks/useRedux';
import { queryKeys } from '@lib/queryKeys';
import { selectAccommodationProperty } from '@store/accommodation';
import { useQueryClient } from '@tanstack/react-query';
import {
  Calendar,
  CalendarDays,
  LayoutList,
  Plus,
  RefreshCw,
} from 'lucide-react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookingCalendar, DailyBookingCalendar } from './BookingCalendar';
import { BookingPreviewPanel } from './BookingPreviewPanel';
import { BookingsPage } from './BookingsPage';

type Tab = 'list' | 'daily' | 'monthly';

export const BookingsIndexPage: React.FC = () => {
  const [tab, setTab] = useState<Tab>('list');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const navigate = useNavigate();
  const property = useAppSelector(selectAccommodationProperty);
  const queryClient = useQueryClient();

  const tabs: Array<{ value: Tab; label: string; icon: React.ReactNode }> = [
    { value: 'list', label: 'List', icon: <LayoutList size={13} /> },
    { value: 'daily', label: 'Daily', icon: <Calendar size={13} /> },
    { value: 'monthly', label: 'Monthly', icon: <CalendarDays size={13} /> },
  ];

  const handleRefresh = async () => {
    if (!property || isRefreshing) return;
    setIsRefreshing(true);
    try {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.accommodation.all,
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <>
      <div className="space-y-4">
        {/* Top bar: tabs + actions */}
        <div className="flex items-center justify-between">
          <div className="flex gap-1">
            {tabs.map((t) => (
              <button
                key={t.value}
                type="button"
                onClick={() => setTab(t.value)}
                className={[
                  'flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium',
                  tab === t.value
                    ? 'bg-pos-primary text-white'
                    : 'border border-gray-200 text-gray-600 hover:bg-gray-50',
                ].join(' ')}
              >
                {t.icon}
                {t.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleRefresh}
              disabled={isRefreshing}
              title="Refresh bookings"
              className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs text-gray-500 hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw
                size={13}
                className={isRefreshing ? 'animate-spin' : ''}
              />
            </button>
            <button
              type="button"
              onClick={() => navigate('/accommodation/bookings/new')}
              className="flex items-center gap-1.5 rounded-xl bg-pos-primary px-3 py-2 text-xs font-medium text-white hover:bg-pos-primary/90"
            >
              <Plus size={13} />
              New booking
            </button>
          </div>
        </div>

        {tab === 'list' && <BookingsPage />}
        {tab === 'daily' && <DailyBookingCalendar />}
        {tab === 'monthly' && <BookingCalendar />}
      </div>

      {/* Slide-in preview panel (always mounted, animates in/out) */}
      <BookingPreviewPanel />
    </>
  );
};
